function getDefaultConfig() {
  let logoutUrl, refreshTokenApiUrl, axiosSource;
  if (
    typeof window !== "undefined" &&
    window &&
    (window.location.hostname.includes("demo") ||
      window.location.hostname.includes("test") ||
      window.location.hostname.includes("localhost"))
  ) {
    const runningScript = document.currentScript;
    logoutUrl = "https://login.fixit-demo.no";
    refreshTokenApiUrl =
      "https://fixitcoreapi.fixit-demo.no/api/v2/Authorization/refresh-token";
    axiosSource = "https://cdn.fixit-demo.no/external/axios/0.27.2/axios.min.js";
    if (runningScript) {
      logoutUrl = runningScript.getAttribute("data-logoutUrl-url") || logoutUrl;
      refreshTokenApiUrl = runningScript.getAttribute("data-refreshTokenApi-url") || refreshTokenApiUrl;
      axiosSource = runningScript.getAttribute("data-axiosSource-url") || axiosSource;
    }
    if (window.location.hostname.includes("test")) {
      logoutUrl = "https://login.test.odindata.no";
      refreshTokenApiUrl = "https://fixitcoreapi.test.odindata.no/api/v2/Authorization/refresh-token";
    }
  } else {
    logoutUrl = "https://login.fixitonline.no";
    refreshTokenApiUrl = "https://fixitcoreapi.fixitonline.no/api/v2/Authorization/refresh-token";
    axiosSource = "https://cdn.fixitonline.no/external/axios/0.27.2/axios.min.js";
  }
  return { logoutUrl, refreshTokenApiUrl, axiosSource };
}

const { logoutUrl: defaultLogoutUrl, refreshTokenApiUrl: defaultRefreshTokenApiUrl, axiosSource: defaultAxiosSource } = getDefaultConfig();

function setupInterceptors(axiosInstance, logoutUrl, refreshTokenApiUrl) {
  let isFetchingRefreshToken = false;
  let failedRequestsQueue = [];

  const processQueue = (error, token = null) => {
    for (const promise of failedRequestsQueue) {
      if (error) promise.reject(error);
      else promise.resolve(token);
    }
    failedRequestsQueue = [];
  };

  if (!axiosInstance.interceptors.response.isRefreshTokenInterceptorSet) {
    axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (err) => {
        const originalRequest = err.config;
        if (err.code === "ECONNABORTED") {
          console.warn("Connection aborted, call failed at: " + err.config.url);
        } else if (
          err.response &&
          err.response.status === 401 &&
          !originalRequest._retry
        ) {
          if (isFetchingRefreshToken) {
            return new Promise(function (resolve, reject) {
              failedRequestsQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers["Authorization"] = "Bearer " + token;
                return axiosInstance(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isFetchingRefreshToken = true;
          return new Promise(function (resolve, reject) {
            axiosInstance
              .post(refreshTokenApiUrl, null, { withCredentials: true })
              .then(({ data }) => {
                const token = data.jwtToken || data.accessToken || data.access_token;
                axiosInstance.defaults.headers.common["Authorization"] = "Bearer " + token;
                originalRequest.headers["Authorization"] = "Bearer " + token;
                processQueue(
                  null,
                  token
                );
                resolve(axiosInstance(originalRequest));
              })
              .catch((err) => {
                processQueue(err, null);
                if (logoutUrl !== "noredirect") {
                  if (typeof window !== "undefined" && window && window.location) {
                    window.location.href =
                      logoutUrl + "?returnUrl=" + window.location.href;
                  }
                }
                reject(err);
              })
              .then(() => {
                isFetchingRefreshToken = false;
              });
          });
        }
        return Promise.reject(err);
      }
    );
    axiosInstance.interceptors.response.isRefreshTokenInterceptorSet = true;
  }
}

/**
 * Loads axios in the window object to be used as window.axios, used when there's no existing axios instance in application, or existing instance is already loaded in the window object.
 * Automatically sets axios interceptors for authorization and authentication.
 * @param {string} logoutUrl - optional, api endpoint to log out the current user, pass "noredirect" {string} if you don't need the logout redirection.
 * @param {string} refreshTokenApiUrl - optional, refresh token api endpoint
 * @param {string} axiosSource - optional, cdn source for the axios script, if the internal fixitcdn ones aren't enough
 * @returns {Promise<unknown>}
 */

//To be used in legacy projects
export async function axiosLoader(
  logoutUrl = defaultLogoutUrl,
  refreshTokenApiUrl = defaultRefreshTokenApiUrl,
  axiosSource = defaultAxiosSource
) {
  if (typeof window !== "undefined" && window && window.axios) {
    setupInterceptors(window.axios, logoutUrl, refreshTokenApiUrl);
  } else {
    return new Promise((resolve) => {
      let script = document.createElement("script");
      script.onload = () => {
        resolve();
        if (window.axios) {
          setupInterceptors(window.axios, logoutUrl, refreshTokenApiUrl);
        }
      };
      script.async = true;
      script.src = axiosSource;
      document.head.appendChild(script);
    });
  }
}

/**
 * Uses existing axios instance that's been passed to it to set axios interceptors for authorization and authentication.
 * @param {Object} axios - axios instance to set authorization and authentication interceptors on
 * @param {string} logoutUrl - optional, api endpoint to log out the current user, pass "noredirect" {string} if you don't need the logout redirection
 * @param {string} refreshTokenApiUrl - optional, refresh token api endpoint
 * @returns {Promise<unknown>}
 */

//To be used with Vue components & Vue projects
export function axiosRefreshTokenSetup(
  axios,
  logoutUrl = defaultLogoutUrl,
  refreshTokenApiUrl = defaultRefreshTokenApiUrl
) {
  setupInterceptors(axios, logoutUrl, refreshTokenApiUrl);
  return true;
}
